/**
 * WebSocket Event Stream Hook — Singleton WS client for real-time events.
 * Phase 6: Centralized WebSocket connection for all live updates.
 *
 * Features:
 * - Singleton WebSocket connection (one per app)
 * - Automatic reconnect with exponential backoff
 * - Channel-based subscriptions
 * - Connection status tracking
 * - Heartbeat/ping-pong support
 */

import { useState, useEffect, useCallback, useRef, useSyncExternalStore } from "react";

// ============================================================================
// Types
// ============================================================================

export type ChannelName =
  | "stats"
  | "logs"
  | "chat"
  | "notifications"
  | "gym"
  | "claude-auth";

export interface StatsEvent {
  channel: "stats";
  data: {
    cpu: number;
    mem: number;
    disk: number;
    uptime: number;
    loadAvg: [number, number, number];
    timestamp: number;
  };
}

export interface LogEvent {
  channel: "logs";
  data: {
    source: string;
    level: string;
    message: string;
    timestamp: number;
  };
}

export interface ChatEvent {
  channel: "chat";
  data: {
    sessionId: number;
    event: "message_added" | "streaming" | "completed";
    payload?: unknown;
  };
}

export interface NotificationEvent {
  channel: "notifications";
  data: {
    id: number;
    title: string;
    body: string | null;
    level: string;
    timestamp: number;
  };
}

export interface GymEvent {
  channel: "gym";
  data: {
    event: "set_added" | "set_updated" | "set_deleted" | "session_started" | "session_ended";
    sessionId: number;
    payload?: unknown;
  };
}

export interface ClaudeAuthEvent {
  channel: "claude-auth";
  data: {
    event: "expiring_soon" | "expired" | "reauth_success";
    expiresInHours?: number;
  };
}

export type ServerEvent =
  | StatsEvent
  | LogEvent
  | ChatEvent
  | NotificationEvent
  | GymEvent
  | ClaudeAuthEvent;

type EventCallback<T extends ServerEvent = ServerEvent> = (event: T) => void;

// ============================================================================
// WebSocket Manager (Singleton)
// ============================================================================

type ConnectionState = "connecting" | "connected" | "disconnected" | "reconnecting";

interface Subscription {
  channel: ChannelName;
  callback: EventCallback;
}

class WebSocketManager {
  private ws: WebSocket | null = null;
  private subscriptions = new Map<string, Set<Subscription>>();
  private connectionState: ConnectionState = "disconnected";
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastPongTime = 0;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private listeners = new Set<() => void>();

  // Backoff config
  private readonly INITIAL_BACKOFF_MS = 1000;
  private readonly MAX_BACKOFF_MS = 30000;
  private readonly BACKOFF_MULTIPLIER = 2;

  constructor() {
    // Auto-connect on first subscription
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getState(): ConnectionState {
    return this.connectionState;
  }

  isConnected(): boolean {
    return this.connectionState === "connected";
  }

  private getWsUrl(): string {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host;
    return `${protocol}//${host}/api/events/ws`;
  }

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    if (this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }

    this.connectionState = "connecting";
    this.notifyListeners();

    try {
      this.ws = new WebSocket(this.getWsUrl());

      this.ws.onopen = () => {
        console.log("[EventStream] Connected");
        this.connectionState = "connected";
        this.reconnectAttempts = 0;
        this.lastPongTime = Date.now();
        this.notifyListeners();

        // Re-subscribe to all channels
        this.resubscribeAll();

        // Start ping interval (check connection health)
        this.startPingInterval();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Handle ping/pong
          if (data.type === "ping") {
            this.ws?.send(JSON.stringify({ type: "pong" }));
            this.lastPongTime = Date.now();
            return;
          }

          // Handle subscription confirmations
          if (data.type === "subscribed" || data.type === "unsubscribed") {
            console.log(`[EventStream] ${data.type}: ${data.channel}`);
            return;
          }

          // Handle connection confirmation
          if (data.type === "connected") {
            console.log("[EventStream] Server confirmed connection", data.channels);
            return;
          }

          // Handle errors
          if (data.type === "error") {
            console.error("[EventStream] Error:", data.message);
            return;
          }

          // Handle channel events
          if (data.channel) {
            this.dispatchEvent(data as ServerEvent);
          }
        } catch (err) {
          console.error("[EventStream] Failed to parse message:", err);
        }
      };

      this.ws.onclose = (event) => {
        console.log(`[EventStream] Disconnected (code: ${event.code})`);
        this.ws = null;
        this.stopPingInterval();

        if (this.connectionState !== "disconnected") {
          this.connectionState = "reconnecting";
          this.notifyListeners();
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error("[EventStream] WebSocket error:", error);
      };
    } catch (err) {
      console.error("[EventStream] Failed to create WebSocket:", err);
      this.connectionState = "disconnected";
      this.notifyListeners();
      this.scheduleReconnect();
    }
  }

  disconnect(): void {
    this.connectionState = "disconnected";
    this.notifyListeners();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.stopPingInterval();

    if (this.ws) {
      this.ws.close(1000, "Client disconnect");
      this.ws = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimeout) {
      return;
    }

    const backoff = Math.min(
      this.INITIAL_BACKOFF_MS * Math.pow(this.BACKOFF_MULTIPLIER, this.reconnectAttempts),
      this.MAX_BACKOFF_MS
    );

    console.log(`[EventStream] Reconnecting in ${backoff}ms (attempt ${this.reconnectAttempts + 1})`);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.reconnectAttempts++;
      this.connect();
    }, backoff);
  }

  private startPingInterval(): void {
    this.stopPingInterval();
    // Check connection health every 30s
    this.pingInterval = setInterval(() => {
      // If no pong in 60s, connection is dead
      if (Date.now() - this.lastPongTime > 60000) {
        console.warn("[EventStream] Connection seems dead, reconnecting...");
        this.ws?.close();
      }
    }, 30000);
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private resubscribeAll(): void {
    const channels = new Set<ChannelName>();
    for (const subs of this.subscriptions.values()) {
      for (const sub of subs) {
        channels.add(sub.channel);
      }
    }

    if (channels.size > 0 && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          type: "subscribe",
          channels: Array.from(channels),
        })
      );
    }
  }

  private dispatchEvent(event: ServerEvent): void {
    const subs = this.subscriptions.get(event.channel);
    if (subs) {
      for (const sub of subs) {
        try {
          sub.callback(event);
        } catch (err) {
          console.error(`[EventStream] Callback error for ${event.channel}:`, err);
        }
      }
    }
  }

  addSubscription(channel: ChannelName, callback: EventCallback): () => void {
    const id = `${channel}-${Date.now()}-${Math.random()}`;
    const subscription: Subscription = { channel, callback };

    if (!this.subscriptions.has(channel)) {
      this.subscriptions.set(channel, new Set());
    }
    this.subscriptions.get(channel)!.add(subscription);

    // Connect if not already connected
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.connect();
    } else {
      // Subscribe to this channel on the server
      this.ws.send(
        JSON.stringify({
          type: "subscribe",
          channels: [channel],
        })
      );
    }

    // Return unsubscribe function
    return () => {
      const subs = this.subscriptions.get(channel);
      if (subs) {
        subs.delete(subscription);
        if (subs.size === 0) {
          this.subscriptions.delete(channel);

          // Unsubscribe from server
          if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(
              JSON.stringify({
                type: "unsubscribe",
                channels: [channel],
              })
            );
          }
        }
      }

      // Disconnect if no more subscriptions
      if (this.subscriptions.size === 0) {
        // Keep connection alive for a bit in case more subscriptions come
        setTimeout(() => {
          if (this.subscriptions.size === 0) {
            this.disconnect();
          }
        }, 5000);
      }
    };
  }
}

// Singleton instance
const wsManager = new WebSocketManager();

// ============================================================================
// React Hooks
// ============================================================================

/**
 * Hook to get the current connection state.
 */
export function useEventStreamStatus(): {
  isConnected: boolean;
  state: ConnectionState;
} {
  const state = useSyncExternalStore(
    (cb) => wsManager.subscribe(cb),
    () => wsManager.getState()
  );

  return {
    isConnected: state === "connected",
    state,
  };
}

/**
 * Hook to subscribe to a specific channel.
 * Callback is called whenever an event arrives on that channel.
 */
export function useEventChannel<T extends ServerEvent>(
  channel: T["channel"],
  callback: (event: T) => void
): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const unsubscribe = wsManager.addSubscription(channel as ChannelName, (event) => {
      callbackRef.current(event as T);
    });

    return unsubscribe;
  }, [channel]);
}

/**
 * Hook to subscribe to stats channel with state management.
 */
export function useStatsStream(
  initialStats?: StatsEvent["data"]
): {
  stats: StatsEvent["data"] | null;
  isConnected: boolean;
} {
  const [stats, setStats] = useState<StatsEvent["data"] | null>(initialStats ?? null);
  const { isConnected } = useEventStreamStatus();

  useEventChannel<StatsEvent>("stats", (event) => {
    setStats(event.data);
  });

  return { stats, isConnected };
}

/**
 * Hook to subscribe to logs channel.
 */
export function useLogsStream(
  maxLines = 500
): {
  logs: LogEvent["data"][];
  isConnected: boolean;
  clearLogs: () => void;
} {
  const [logs, setLogs] = useState<LogEvent["data"][]>([]);
  const { isConnected } = useEventStreamStatus();

  useEventChannel<LogEvent>("logs", (event) => {
    setLogs((prev) => {
      const newLogs = [...prev, event.data];
      if (newLogs.length > maxLines) {
        return newLogs.slice(-maxLines);
      }
      return newLogs;
    });
  });

  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  return { logs, isConnected, clearLogs };
}

/**
 * Hook to subscribe to notifications channel.
 */
export function useNotificationsStream(): {
  latestNotification: NotificationEvent["data"] | null;
  isConnected: boolean;
} {
  const [latestNotification, setLatestNotification] = useState<NotificationEvent["data"] | null>(
    null
  );
  const { isConnected } = useEventStreamStatus();

  useEventChannel<NotificationEvent>("notifications", (event) => {
    setLatestNotification(event.data);
  });

  return { latestNotification, isConnected };
}

/**
 * Hook to subscribe to gym channel.
 */
export function useGymStream(sessionId?: number): {
  latestEvent: GymEvent["data"] | null;
  isConnected: boolean;
} {
  const [latestEvent, setLatestEvent] = useState<GymEvent["data"] | null>(null);
  const { isConnected } = useEventStreamStatus();

  useEventChannel<GymEvent>("gym", (event) => {
    // Filter by sessionId if provided
    if (sessionId === undefined || event.data.sessionId === sessionId) {
      setLatestEvent(event.data);
    }
  });

  return { latestEvent, isConnected };
}

/**
 * Hook to subscribe to claude-auth channel.
 */
export function useClaudeAuthStream(): {
  authEvent: ClaudeAuthEvent["data"] | null;
  isConnected: boolean;
} {
  const [authEvent, setAuthEvent] = useState<ClaudeAuthEvent["data"] | null>(null);
  const { isConnected } = useEventStreamStatus();

  useEventChannel<ClaudeAuthEvent>("claude-auth", (event) => {
    setAuthEvent(event.data);
  });

  return { authEvent, isConnected };
}

/**
 * Manually connect/disconnect the WebSocket.
 */
export function useEventStreamControl(): {
  connect: () => void;
  disconnect: () => void;
} {
  return {
    connect: () => wsManager.connect(),
    disconnect: () => wsManager.disconnect(),
  };
}
